// Imports
import React from "react";
import ReactDOM from "react-dom";
import { Provider as StateProvider } from "react-redux";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import isFunction from "lodash/isFunction";
import axios from 'axios';

// App Imports
import { store } from "./setup/store";
import routes from "./setup/routes";
import { getTokenFromCookie, setUser, setUserInCookie, unsetUserInCookie } from "./modules/user/api/actions/query";
import Layout from "./modules/common/Layout";
import Redirector from "./modules/common/Redirector";
import RoutePrivate from "./modules/auth/RoutePrivate";
import * as serviceWorker from "./serviceWorker";
import { API_URL } from "./setup/config/env";
import { connectToWebSocket } from "./setup/apiWebSocket";
import GoogleProviderWrapper from './setup/config/GoogleProviderWrapper';
import { fetchSettingDetail } from './modules/setting/api/actions/query';
import BrandedLoading from "./modules/common/BrandedLoading";

// Latest UI Library changes
import { ThemeProvider } from "@mui/material/styles";
import theme from "./setup/theme";
import { Box } from "@mui/material";

//--- Date Pickers Providers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Subscribe from "./modules/marketing/components/Subscribe";
import Unsubscribe from "./modules/marketing/components/Unsubscribe";
import CancelBooking from "./modules/tableReservation/components/CancelBooking";
import { messageShow } from "./modules/common/api/actions";
import "ckeditor5/ckeditor5.css";
import "./assets/css/bootstrap.min.css";

// Silence ResizeObserver loop errors
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' || e.message === 'ResizeObserver loop limit exceeded') {
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErr) {
      resizeObserverErr.style.display = 'none';
    }
    // Also stop propagation to prevent the red overlay in development
    e.stopImmediatePropagation();
  }
});

// Ensure HTML5 validation and scrollIntoView take the 64px fixed AppBar into account
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-scroll-padding', 'true');
  styleEl.innerHTML = `
    html {
      scroll-padding-top: 120px !important;
    }
    .MuiFormControl-root, input, textarea, select {
      scroll-margin-top: 120px !important;
    }
    .Mui-error,
    .MuiFormLabel-root.Mui-error,
    .MuiInputLabel-root.Mui-error,
    .MuiFormHelperText-root.Mui-error {
      color: #0d3559 !important;
    }
    .MuiInput-underline.Mui-error:after,
    .MuiInput-underline.Mui-error:before,
    .MuiInput-underline.Mui-error:hover:not(.Mui-disabled):before,
    .MuiFilledInput-underline.Mui-error:after,
    .MuiFilledInput-underline.Mui-error:before,
    .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
      border-color: #0d3559 !important;
      border-bottom-color: #0d3559 !important;
    }
  `;
  document.head.appendChild(styleEl);
}

const root = document.getElementById("root");
ReactDOM.render(
  <StateProvider store={store}>
    <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          width: '100vw',
          bgcolor: '#f8fafc'
        }}
      >
        <BrandedLoading />
      </div>
  </StateProvider>,
  root
);

// User Authentication
const token = getTokenFromCookie()
if (token && token !== "undefined" && token !== "") {
  const user = JSON.parse(window.localStorage.getItem("user"));
  if (user) {
    // Dispatch action
    store.dispatch(setUser(token, user));

    setUserInCookie(token, user);

    if (window.WebSocket) {
      connectToWebSocket(store);
    } else {
      console.log("WebSocket is not supported by your browser.");
    }
    store.dispatch(fetchSettingDetail());
  }
}

axios.interceptors.response.use(
  response => {
    if(response.data.message === "You are not authorized to perform this action."){
      store.dispatch(messageShow("You are not authorized to perform this action."))
      unsetUserInCookie()
      setTimeout(() => {window.location.href = '/login'}, 2500)
      return new Promise(() => {});
    }
    return response
  },
  error => {
    if (error.response && error.response.status === 401) {
      store.dispatch(messageShow("Session Expired, Please Login again!"))
      unsetUserInCookie()
      setTimeout(() => {window.location.href = '/login'}, 2500)
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
)

const fetchOAuthClientId = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/oauth/config`);
    if (response.data.success && response.data.data?.client_id) {
      return response.data.data.client_id;
    }
    throw new Error("Failed to fetch Google client ID.");
  } catch (error) {
    console.error("Error fetching Google client ID:", error);
    return null;
  }
};

// Render App
const renderApp = async () => {
  const clientId = await fetchOAuthClientId();

const path = window.location.pathname;
const id = path.split("/").pop();
const searchParams = new URLSearchParams(window.location.search);
const bookingId = searchParams.get("id");
if (path.includes("/email/unsubscribe")) {
  // window.location.href = `/unsubscribe.html?id=${id}`;
  ReactDOM.render(<Unsubscribe unsubscribeId={id} />, document.getElementById("root"));
} else if (path.includes("/email/subscribe")) {
  // window.location.href = `/subscribe.html?id=${id}`;
  ReactDOM.render(<Subscribe userId={id} />, document.getElementById("root"));
} else if (path.includes("/table-bookings/cancel")) {
  ReactDOM.render(<CancelBooking bookingId={bookingId}/>, document.getElementById("root"));
}
else {
ReactDOM.render(
  <GoogleProviderWrapper clientId={clientId}>
  <StateProvider store={store}>
    <Router>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Layout>
            <Switch>
              {Object.values(routes).map((route, index) =>
                route.auth ? (
                  <RoutePrivate
                    {...route}
                    key={index}
                    path={isFunction(route.path) ? route.path() : route.path}
                  />
                ) : (
                  <Route
                    {...route}
                    key={index}
                    path={isFunction(route.path) ? route.path() : route.path}
                  />
                )
              )}
              <Route component={Redirector} />
            </Switch>
          </Layout>
        </LocalizationProvider>
      </ThemeProvider>
    </Router>
  </StateProvider>,
  </GoogleProviderWrapper>,
  document.getElementById("root")
);
}

// Service Worker
serviceWorker.unregister();
};

renderApp();
